import React, { useRef, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, Float } from "@react-three/drei";
import * as THREE from "three";

// Floating Logo Component with Back-and-Forth Rotation
const FloatingLogo = ({ logoUrl, position }) => {
  const ref = useRef();
  const rotationState = useRef({
    currentAngle: 0,
    targetAngle: Math.PI / 6, // Reduced maximum angle for quicker transitions
    direction: 1,
    speed: 0.01 // Increased speed for more responsive rotation
  });

  useFrame((state, delta) => {
    if (ref.current) {
      const rotation = rotationState.current;
      
      // Update rotation
      rotation.currentAngle += rotation.speed * rotation.direction * delta * 60;
      
      // Apply rotation to mesh
      ref.current.rotation.y = rotation.currentAngle;
      
      // Change direction sooner with smoother transition
      if (Math.abs(rotation.currentAngle) >= rotation.targetAngle) {
        rotation.direction *= -1;
      }
    }
  });

  // Load texture
  const texture = new THREE.TextureLoader().load(logoUrl);

  return (
    <Float speed={1} rotationIntensity={0.4} floatIntensity={1}>
      <mesh ref={ref} position={position}>
        <planeGeometry args={[2.2, 2.2]} />
        <meshBasicMaterial map={texture} transparent alphaTest={0.1} side={THREE.DoubleSide} />
      </mesh>
    </Float>
  );
};

// Skill logos with moderately spread positions
const skills = [

  {
    logo: "https://upload.wikimedia.org/wikipedia/commons/a/a7/React-icon.svg",
    position: [-9, 6, 0],
  },
  {
    logo: "https://cdn.worldvectorlogo.com/logos/next-js.svg",
    position: [-3, 6, 0],
  },
  {
    logo: "https://cdn.worldvectorlogo.com/logos/django.svg",
    position: [3, 6, 0],
  },
  {
    logo: "https://cdn.worldvectorlogo.com/logos/aws-2.svg",
    position: [9, 6, 0],
  },

  // Second row
  {
    logo: "https://cdn.worldvectorlogo.com/logos/java-14.svg",
    position: [-9, 2, 0],
  },
  {
    logo: "https://cdn.worldvectorlogo.com/logos/python-5.svg",
    position: [-3, 2, 0],
  },
  {
    logo: "https://cdn.worldvectorlogo.com/logos/c-1.svg",
    position: [3, 2, 0],
  },
  {
    logo: "https://cdn.worldvectorlogo.com/logos/spring-3.svg",
    position: [9, 2, 0],
  },

  // Third row
  {
    logo: "https://cdn.worldvectorlogo.com/logos/angular-icon-1.svg",
    position: [-10, -2, 0],
  },
  {
    logo: "https://cdn.worldvectorlogo.com/logos/tailwind-css-2.svg",
    position: [-5, -2, 0],
  },
  {
    logo: "https://cdn.worldvectorlogo.com/logos/mysql-logo.svg",
    position: [0, -2, 0],
  },
  {
    logo: "https://cdn.worldvectorlogo.com/logos/postgresql.svg",
    position: [5, -2, 0],
  },
  {
    logo: "https://cdn.worldvectorlogo.com/logos/git-icon.svg",
    position: [10, -2, 0],
  },

  // Fourth row
  {
    logo: "https://cdn.worldvectorlogo.com/logos/mongodb-icon-1.svg",
    position: [-9, -6, 0],
  },
  {
    logo: "https://cdn.worldvectorlogo.com/logos/docker.svg",
    position: [-3, -6, 0],
  },
  {
    logo: "https://cdn.worldvectorlogo.com/logos/threejs-1.svg",
    position: [3, -6, 0],
  },
  {
    logo: "https://cdn.worldvectorlogo.com/logos/nginx-1.svg",
    position: [9, -6, 0],
  },
];

export const Skills3D = () => {
  return (
    <section className="skills-section" id="skills">
      <h2>Skills</h2>
      <div style={{ height: "700px", width: "100%", marginTop: "10px" }}>
        <Canvas camera={{ position: [0, 0, 18] }}>
          {" "}
          {/* Balanced camera distance */}
          <ambientLight intensity={0.8} />
          <pointLight position={[12, 12, 12]} intensity={1} />
          {skills.map((skill, index) => (
            <FloatingLogo
              key={index}
              logoUrl={skill.logo}
              position={skill.position}
            />
          ))}
          <OrbitControls enableZoom={false} autoRotate autoRotateSpeed={0.12} />
        </Canvas>
      </div>
    </section>
  );
};

export { Skills3D as Skills };
